import { useState, useCallback, useRef, useEffect } from 'react';

interface MessageInputProps {
  onSend: (text: string) => void;
  disabled?: boolean;
}

export function MessageInput({ onSend, disabled = false }: MessageInputProps): React.JSX.Element {
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = useCallback(() => {
    const trimmed = text.trim();
    if (trimmed && !disabled) {
      onSend(trimmed);
      setText('');
    }
  }, [text, disabled, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  // Auto-resize textarea
  useEffect(() => {
    const textarea = inputRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 150).toString()}px`;
    }
  }, [text]);

  return (
    <div className="flex items-end gap-2 border-t border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-telegram-bg-dark">
      <textarea
        ref={inputRef}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Type a message..."
        disabled={disabled}
        rows={1}
        className="max-h-[150px] min-h-[40px] flex-1 resize-none rounded-2xl border border-gray-300 bg-telegram-secondary-bg px-4 py-2 text-sm focus:border-telegram-blue focus:outline-none focus:ring-1 focus:ring-telegram-blue disabled:opacity-50 dark:border-gray-600 dark:bg-telegram-secondary-bg-dark"
      />
      <button
        onClick={handleSend}
        disabled={disabled || !text.trim()}
        className="flex h-10 w-10 items-center justify-center rounded-full bg-telegram-blue text-white hover:bg-telegram-blue-dark disabled:opacity-50"
        aria-label="Send message"
      >
        {'\u27A4'}
      </button>
    </div>
  );
}
